import type { DocumentRow } from '../api/types'

export type GroupBy = 'vendor' | 'product' | 'product_type' | 'date' | 'status' | 'none'

/** Commercial first, then samples. */
export const PRODUCT_TYPE_ORDER = ['commercial', 'sample'] as const
export const PRODUCT_TYPE_LABEL: Record<string, string> = {
  commercial: 'Commercial products',
  sample: 'Sample products',
}

const STATUS_LABEL: Record<string, string> = {
  complete: 'Complete',
  missing_spi: 'Missing SPI',
  missing_vendor: 'Missing vendor',
}

export interface DocumentGroup {
  key: string
  label: string
  items: DocumentRow[]
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/**
 * Bucket rows into groups. Group order:
 *   - vendor: alphabetical; "(no vendor)" last
 *   - product: alphabetical
 *   - date: by expiration month YYYY-MM descending; "(no expiry)" last
 *   - none: a single group with empty label
 */
export function groupDocuments(
  rows: readonly DocumentRow[],
  mode: GroupBy,
): DocumentGroup[] {
  if (mode === 'none') {
    return rows.length > 0 ? [{ key: 'all', label: '', items: [...rows] }] : []
  }

  const buckets = new Map<string, DocumentRow[]>()

  for (const row of rows) {
    const key = bucketKey(row, mode)
    const list = buckets.get(key) ?? []
    list.push(row)
    buckets.set(key, list)
  }

  const entries = Array.from(buckets.entries()).map(([key, items]) => ({
    key,
    label: labelFor(key, mode),
    items,
  }))

  entries.sort((a, b) => sortGroups(a, b, mode))
  return entries
}

function bucketKey(row: DocumentRow, mode: GroupBy): string {
  if (mode === 'vendor') return row.vendor_name?.trim() || '__no_vendor__'
  if (mode === 'product') return row.product_name?.trim() || '__no_product__'
  if (mode === 'product_type') return row.is_sample ? 'sample' : 'commercial'
  if (mode === 'date') {
    if (!row.expiration_date) return '__no_expiry__'
    return row.expiration_date.slice(0, 7)
  }
  if (mode === 'status') return row.status
  return 'all'
}

function labelFor(key: string, mode: GroupBy): string {
  if (key === '__no_vendor__') return '(no vendor)'
  if (key === '__no_product__') return '(no product)'
  if (key === '__no_expiry__') return '(no expiry)'
  if (mode === 'status') return STATUS_LABEL[key] ?? key
  if (mode === 'product_type') return PRODUCT_TYPE_LABEL[key] ?? key
  if (mode === 'date' && /^\d{4}-\d{2}$/.test(key)) {
    const [y, m] = key.split('-')
    const month = MONTHS[Number(m) - 1] ?? m ?? ''
    return `${month} ${y}`
  }
  return key
}

function sortGroups(a: DocumentGroup, b: DocumentGroup, mode: GroupBy): number {
  const aIsPlaceholder = a.key.startsWith('__')
  const bIsPlaceholder = b.key.startsWith('__')
  if (aIsPlaceholder && !bIsPlaceholder) return 1
  if (!aIsPlaceholder && bIsPlaceholder) return -1

  if (mode === 'date') {
    return b.key.localeCompare(a.key) // descending
  }
  if (mode === 'product_type') {
    return PRODUCT_TYPE_ORDER.indexOf(a.key as 'commercial') - PRODUCT_TYPE_ORDER.indexOf(b.key as 'commercial')
  }
  return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
}
